import { get, set, del } from 'idb-keyval';
import { nanoid } from 'nanoid';
import { create } from 'zustand';
import { persist, createJSONStorage, subscribeWithSelector } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { PreferenceDict, PreferenceKey, PreferenceState, StoredPreference } from '../types';

const createIndexedDBStorage = () => {
  return {
    getItem: async (name: string): Promise<string | null> => {
      try {
        const value = await get(name);
        return value;
      } catch (error) {
        console.error('Failed to get item from IndexedDB:', error);
        return null;
      }
    },

    setItem: async (name: string, value: string): Promise<void> => {
      try {
        await set(name, value);
      } catch (error) {
        console.error('Failed to set item in IndexedDB:', error);
        throw error;
      }
    },

    removeItem: async (name: string): Promise<void> => {
      try {
        await del(name);
      } catch (error) {
        console.error('Failed to remove item from IndexedDB:', error);
        throw error;
      }
    },
  };
};

export const usePreferenceStore = create<PreferenceState>()(
  subscribeWithSelector(
    persist(
      immer((_set) => ({
        prefs: {},
        clientId: nanoid(),
        isHydrated: false,
        isBootstrapped: false,
        isDataInitialized: false,
        isDataError: false,
        _internal_isReady: false,
        lazyLoaded: new Set(),
      })),
      {
        name: 'user-preferences-state',
        storage: createJSONStorage(() => createIndexedDBStorage()),
        partialize: (state) => ({
          prefs: state.prefs,
        }),
        onRehydrateStorage: () => (state) => {
          if (state) {
            state.isHydrated = true;
          }
        },

        /** We want to hydrate the store manually, as we need to update the name of the store to include
      the current user ID 
      @example
      ```
      useBoundStore.persist.setOptions({
        name: 'user-preferences-state',
      });

      // and then we can call the hydrate function manually
      await useBoundStore.persist.rehydrate()
      ```
      */
        skipHydration: true,
      }
    )
  )
);

export const preferenceActions = {
  setPreference: <K extends PreferenceKey>(
    id: K,
    value: PreferenceDict[K],
    updatedAt: string = new Date().toISOString()
  ) => {
    usePreferenceStore.setState((state) => {
      state.prefs[id] = { id, value, updatedAt };
    });
  },

  setPreferences: (prefs: Record<string, StoredPreference>) => {
    usePreferenceStore.setState((state) => {
      state.prefs = { ...state.prefs, ...prefs };
    });
  },

  setDataInitialized: (initialized: boolean) => {
    usePreferenceStore.setState((state) => {
      state.isDataInitialized = initialized;
    });
  },

  setIsDataError: (failed: boolean) => {
    usePreferenceStore.setState((state) => {
      state.isDataError = failed;
    });
  },

  setIsReady: (ready: boolean) => {
    usePreferenceStore.setState((state) => {
      state._internal_isReady = ready;
    });
  },

  setBootstrapped: (bootstrapped: boolean) => {
    usePreferenceStore.setState((state) => {
      state.isBootstrapped = bootstrapped;
    });
  },

  updateFromWebSocket: (id: string, preference: StoredPreference) => {
    usePreferenceStore.setState((state) => {
      const existing = state.prefs[id];
      if (!existing || new Date(preference.updatedAt) > new Date(existing.updatedAt)) {
        state.prefs[id] = preference;
      }
    });
  },

  updatePreferencesFromWebSocket: (changedSettings: Record<string, StoredPreference>) => {
    usePreferenceStore.setState((state) => {
      state.prefs = { ...state.prefs, ...changedSettings };
    });
  },
  setLazyLoaded: (id: string) => {
    usePreferenceStore.setState((state) => {
      state.lazyLoaded.add(id);
    });
  },

  isLazyLoaded: (id: string): boolean => {
    return usePreferenceStore.getState().lazyLoaded.has(id);
  },
};
